/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";

/*import url and gateway methods */
import { postData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const AddMultipleLeadsModal = ({ handleClose = () => {} }) => {
  // const moduleAccess = localStorage.getItem("moduleaccess");
  const token = localStorage.getItem("token");
  // const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  // Default lead state
  const defaultLeadState = [
    { name: "", surname: "", email: "", phone: "", company: "", notes: "" },
  ];

  const [leads, setLeads] = useState(defaultLeadState);

  // New state to track if we should show duplicate indicators
  const [showDuplicateIndicators, setShowDuplicateIndicators] = useState(false);

  // Create a ref array to store references to name input fields
  const nameInputRefs = useRef([]);

  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleInputChange = (index, field, value) => {
    const updatedLeads = [...leads];
    updatedLeads[index][field] = value;
    setLeads(updatedLeads);

    // Clear error when user edits
    setErrorMessage("");
    setShowDuplicateIndicators(false);
  };

  const handleDeleteRow = (index) => {
    const updatedLeads = leads.filter((_, i) => i !== index);
    setLeads(updatedLeads);
    setErrorMessage(""); // Clear error on delete
    setShowDuplicateIndicators(false);
  };

  const handleAddRow = () => {
    // Validate last row only if it's not the first row
    if (leads.length > 0) {
      setShowDuplicateIndicators(true);

      const lastRow = leads[leads.length - 1];
      const lastEmail = lastRow.email.trim();

      // Check if email exists in any previous row
      if (lastEmail) {
        const duplicateFound = leads
          .slice(0, -1)
          .some((lead) => lead.email.trim() === lastEmail);

        if (duplicateFound) {
          setErrorMessage(
            `The email "${lastEmail}" is already entered. Please enter a different email.`
          );
          return;
        }
      }
    }

    // Clear error if no duplicates
    setErrorMessage("");

    // Proceed to add new row
    const newLeads = [
      ...leads,
      { name: "", surname: "", email: "", phone: "", company: "", notes: "" },
    ];

    setLeads(newLeads);

    // Focus on new row
    setTimeout(() => {
      const newIndex = newLeads.length - 1;
      if (nameInputRefs.current[newIndex]) {
        nameInputRefs.current[newIndex].focus();
      }
    }, 0);
  };

  // Create a custom close handler that resets the leads state
  const resetHandler = () => {
    setLeads(defaultLeadState);
    setShowDuplicateIndicators(false);
    setErrorMessage("");
  };

  // Compute duplicate email indices
  const duplicateEmailIndices = useMemo(() => {
    if (!showDuplicateIndicators) return new Set();

    const duplicates = new Set();
    const seenEmails = new Set();

    leads.forEach((lead, index) => {
      const email = lead.email.trim().toLowerCase();
      if (email) {
        if (seenEmails.has(email)) {
          duplicates.add(index);
          // Also mark the first occurrence
          leads.forEach((l, i) => {
            if (l.email.trim().toLowerCase() === email && i !== index) {
              duplicates.add(i);
            }
          });
        } else {
          seenEmails.add(email);
        }
      }
    });

    return duplicates;
  }, [leads, showDuplicateIndicators]);

  // Update refs when leads array changes
  useEffect(() => {
    // Pre-allocate ref array based on leads length
    nameInputRefs.current = nameInputRefs.current.slice(0, leads.length);
  }, [leads.length]);

  const handleKeyPress = (index, field, event) => {
    if (field === "notes" && event.key === "Enter") {
      handleAddRow();
    }
  };

  // Function to handle saving leads . Send leads using POST method to API_SAVE_QUICK_LEAD
  const handleSave = async () => {
    const isValid = leads.every(
      (lead) => lead.name.trim() && lead.email.trim()
    );

    if (!isValid) {
      alert("Please fill in both Name and Email for all rows.");
      return;
    }

    // Check for duplicate emails
    const emails = leads.map((lead) => lead.email.trim());
    const uniqueEmails = new Set(emails);

    if (emails.length !== uniqueEmails.size) {
      alert("Duplicate emails found. Please correct them before saving.");
      return;
    }

    const leadsData = {
      leads: leads,
    };

    try {
      setIsSaving(true);

      const requestUrl =
        url.API_BASE_URL + url.API_SAVE_QUICK_LEAD + `?token=${token}`;

      const response = await postData(requestUrl, leadsData);

      console.log("response", response);
      setIsSaving(false);

      if (response.status) {
        // Close the modal
        const bootstrapModal = document.querySelector("#addMultipleLeads");
        const modal = bootstrap.Modal.getInstance(bootstrapModal);
        modal.hide();
        console.log("Leads saved successfully:", response.data);
        setLeads(defaultLeadState);
        resetHandler();
        handleClose(response.data);
      } else {
        setErrorMessage(response.message || "Failed to save leads.");
      }
    } catch (error) {
      console.error("Error saving leads:", error);
    }
  };

  return (
    <div
      className="modal fade"
      id="addMultipleLeads"
      tabIndex="-1"
      aria-labelledby="addMultipleLeadsLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-xl" style={{ maxWidth: "90vw" }}>
        <div className="modal-content bg-white border-0 rounded-15 shadow-lg">
          {/* Enhanced Header with Icon and Gradient Background */}
          <div className="modal-header bg-primary text-white px-4 py-3 border-bottom border-gray-300">
            <div className="d-flex align-items-center gap-3">
              <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
                <span className="material-symbols-outlined text-primary fs-5 icon-md">
                  group_add
                </span>
              </div>
              <div>
                <h3
                  className="fw-bold fs-2 text-white mb-0"
                  id="addMultipleLeadsLabel"
                >
                  Add Multiple Leads
                </h3>
                <p className="text-white fs-sm mb-0">
                  Add more than one leads at once using this feature.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={resetHandler}
            ></button>
          </div>
          <div className="modal-body p-4">
            <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
              {isSaving && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-white bg-opacity-75"
                  style={{ zIndex: 1050 }}
                >
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-semibold mb-0">
                    Saving leads, please wait...
                  </p>
                </div>
              )}
              <form onSubmit={(e) => e.preventDefault()}>
                {errorMessage && (
                  <div className="alert alert-danger mt-2" role="alert">
                    {errorMessage}
                  </div>
                )}
                <div className="table-wrapper">
                  <div className="table-responsive mb-1">
                    <table className="table align-middle fs-sm text-nowrap border-0 mb-0">
                      <thead>
                        <tr>
                          <th
                            width="15%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            Name
                          </th>
                          <th
                            width="15%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            Surname
                          </th>
                          <th
                            width="20%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            Email
                          </th>
                          <th
                            width="15%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            Phone
                          </th>
                          <th
                            width="15%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            Company
                          </th>
                          <th
                            width="15%"
                            className="bg-transparent fs-md fw-semibold border-0"
                          >
                            Notes
                          </th>
                          <th className="bg-transparent fs-md fw-semibold border-0">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead, index) => (
                          <tr key={index} style={{ verticalAlign: "top" }}>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="text"
                                className="form-control bg-white shadow-none border-gray-300"
                                placeholder="Enter name"
                                value={lead.name}
                                ref={(el) =>
                                  (nameInputRefs.current[index] = el)
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="text"
                                className="form-control bg-white shadow-none border-gray-300"
                                placeholder="Enter surname"
                                value={lead.surname}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "surname",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td
                              className="border-bottom-0 p-1"
                              style={{ position: "relative" }}
                            >
                              <div style={{ position: "relative" }}>
                                <input
                                  type="email"
                                  className="form-control bg-white shadow-none border-gray-300"
                                  placeholder="Enter email"
                                  value={lead.email}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "email",
                                      e.target.value
                                    )
                                  }
                                />
                                {duplicateEmailIndices.has(index) && (
                                  <p
                                    className="text-danger fw-bold fs-xl position-absolute"
                                    style={{
                                      right: "8px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                    }}
                                  >
                                    *
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="text"
                                className="form-control bg-white shadow-none border-gray-300"
                                placeholder="Enter phone"
                                value={lead.phone}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "phone",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="text"
                                className="form-control bg-white shadow-none border-gray-300"
                                placeholder="Enter company"
                                value={lead.company}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "company",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td className="border-bottom-0 p-1">
                              <input
                                type="text"
                                className="form-control bg-white shadow-none border-gray-300"
                                placeholder="Enter notes"
                                value={lead.notes}
                                onChange={(e) =>
                                  handleInputChange(
                                    index,
                                    "notes",
                                    e.target.value
                                  )
                                }
                                onKeyPress={(e) =>
                                  handleKeyPress(index, "notes", e)
                                }
                              />
                            </td>
                            <td className="action border-bottom-0">
                              <Link
                                to="#"
                                className="text-danger"
                                onClick={() => handleDeleteRow(index)}
                              >
                                <span className="d-block material-symbols-outlined">
                                  delete
                                </span>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    className="btn btn-outline-primary mt-2"
                    onClick={handleAddRow}
                  >
                    + Add New
                  </button>
                </div>
                <div className="action d-flex justify-content-end pt-3 border-top mt-4">
                  <button
                    onClick={handleSave}
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-2"
                    disabled={isSaving}
                    style={{
                      cursor: isSaving ? "not-allowed" : "pointer",
                    }}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined me-1">
                          save
                        </span>{" "}
                        Save Leads
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMultipleLeadsModal;
